import React from 'react';
import { Chip, useTheme, Theme } from '@mui/material';
import { COLORS } from '../../utils/colors';

type RevenueStatusType = 'agileEngineRevenue' | 'clientGrowth';
type StatusValue = 'Decreasing' | 'Growing' | 'Flat' | string | null;

export enum AgileEngineRevenue {
  FLAT = 'FLAT',
  GROWING = 'GROWING',
  DECREASING = 'DECREASING',
  ENDED = 'ENDED',
  CLOSED = 'CLOSED',
}

export enum ClientGrowth {
  FLAT = 'FLAT',
  GROWING = 'GROWING',
  DECREASING = 'DECREASING',
  NO_INFO = 'NO_INFO',
}

export const agileEngineRevenueLabels: Record<AgileEngineRevenue, string> = {
  [AgileEngineRevenue.FLAT]: 'Flat',
  [AgileEngineRevenue.GROWING]: 'Growing',
  [AgileEngineRevenue.DECREASING]: 'Decreasing',
  [AgileEngineRevenue.ENDED]: 'Ended',
  [AgileEngineRevenue.CLOSED]: 'Closed',
};

export const clientGrowthLabels: Record<ClientGrowth, string> = {
  [ClientGrowth.FLAT]: 'Flat',
  [ClientGrowth.GROWING]: 'Growing',
  [ClientGrowth.DECREASING]: 'Decreasing',
  [ClientGrowth.NO_INFO]: 'No info',
};

interface RevenueStatusChipProps {
  value: StatusValue;
  label: string;
  type: RevenueStatusType;
  size?: 'small' | 'medium';
}

const getChipColor = (
  type: RevenueStatusType,
  value: StatusValue,
): 'error' | 'success' | 'warning' | 'default' => {
  if (type === 'agileEngineRevenue') {
    if (value === AgileEngineRevenue.DECREASING) return 'error';
    if (value === AgileEngineRevenue.GROWING) return 'success';
    else return 'warning';
  }
  if (type === 'clientGrowth') {
    if (value === ClientGrowth.DECREASING) return 'error';
    if (value === ClientGrowth.FLAT) return 'warning';
    if (value === ClientGrowth.GROWING) return 'success';
    if (value === ClientGrowth.NO_INFO) return 'default';
  }
  return 'default';
};

const getChipBgColor = (
  type: RevenueStatusType,
  value: StatusValue,
  theme: Theme,
): string | null => {
  if (type === 'agileEngineRevenue') {
    if (value === AgileEngineRevenue.DECREASING)
      return theme.palette.highlight?.error;
    if (value === AgileEngineRevenue.GROWING)
      return theme.palette.highlight?.success;
    if (value === AgileEngineRevenue.FLAT) return COLORS.HIGHLIGHTS.PROMINENT;
    if (
      value === AgileEngineRevenue.CLOSED ||
      value === AgileEngineRevenue.ENDED
    )
      return COLORS.HIGHLIGHTS.NEUTRAL;
  }
  if (type === 'clientGrowth') {
    if (value === ClientGrowth.FLAT) return COLORS.HIGHLIGHTS.PROMINENT;
    if (value === ClientGrowth.GROWING) return theme.palette.highlight?.success;
    if (value === ClientGrowth.DECREASING)
      return theme.palette.highlight?.error;
    if (value === ClientGrowth.NO_INFO) return COLORS.HIGHLIGHTS.NEUTRAL;
  }
  return null;
};

const getChipTextColor = (
  colorType: 'error' | 'success' | 'warning' | 'default',
  theme: Theme,
): string => {
  switch (colorType) {
    case 'error':
      return theme.palette.error.main;
    case 'success':
      return theme.palette.success.main;
    case 'default':
      return COLORS.GRAY.LIGHTSLATE;
    default:
      return 'inherit';
  }
};

export const RevenueStatusChip: React.FC<RevenueStatusChipProps> = ({
  value,
  label,
  type,
  size = 'small',
}) => {
  const theme = useTheme();
  const stringValue = value?.toString() ?? null;
  const colorType = getChipColor(type, stringValue);
  return (
    <Chip
      size={size}
      label={label}
      sx={{
        backgroundColor: getChipBgColor(type, stringValue, theme),
        color: getChipTextColor(colorType, theme),
      }}
    />
  );
};
